import {
  Button,
  Divider,
  Flex,
  HStack,
  Icon,
  Image,
  Link as ChakraLink,
  Stack,
  Text,
  useBreakpointValue,
  useColorMode,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import React from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { LuAtSign, LuKeyRound, LuMoon, LuSun } from 'react-icons/lu';
import { Link } from 'react-router-dom';
import { z } from 'zod';
import logoBlue from '../../assets/SignIn/logo.png';
import logoWhite from '../../assets/SignIn/logo.svg';
import { Input } from '../../components/shared/Input';
import { useAuth } from '../../hooks/auth';

const signInFormSchema = z.object({
  email: z.string().nonempty('E-mail obrigatório').email('E-mail inválido'),
  password: z.string().nonempty('Senha obrigatória'),
});
type SignInFormData = z.infer<typeof signInFormSchema>;

const SignIn: React.FC = () => {
  const { register, handleSubmit, formState, watch } = useForm({
    resolver: zodResolver(signInFormSchema),
  });

  const { isSubmitting, errors } = formState;

  const { signIn } = useAuth();

  const toast = useToast();

  const isWideVersion = useBreakpointValue({
    base: false,
    sm: true,
    md: true,
    lg: true,
  });

  const { colorMode, toggleColorMode } = useColorMode();

  const bg = useColorModeValue('blue.600', 'gray.800');
  const bgWideVersion = useColorModeValue('white', 'gray.800');
  const bgForm = useColorModeValue('white', 'gray.700');
  const bgFormWideVersion = useColorModeValue('white', 'gray.800');
  const color = useColorModeValue('gray', 'white');
  const colorIcon = useColorModeValue('blue.500', 'white');
  const link = useColorModeValue('blue.600', 'white');

  const handleSignIn: SubmitHandler<SignInFormData> = async ({
    email,
    password,
  }) => {
    try {
      // Com o retorno do signIn, podemos habilitar os demais campos do formulário
      const result = await signIn({ email, password });
      toast({
        title: result.message,
        status: 'info',
        isClosable: true,
        position: 'bottom-right',
      });
    } catch (error) {
      toast({
        title: `Endereço de e-mail e/ou senha incorreta.`,
        status: 'error',
        isClosable: true,
        position: 'bottom-right',
      });
    }
  };

  return (
    <Flex
      w="100vw"
      h="100vh"
      flexDir="column"
      align="center"
      justify="center"
      bg={[bgWideVersion, bg]}
    >
      {(isWideVersion || colorMode !== 'light') && (
        <Image src={logoWhite} alt="Okton - Sistemas Empresariais" mb="8" />
      )}

      {colorMode === 'light' && !isWideVersion && (
        <Image
          src={logoBlue}
          alt="Okton - Sistemas Empresariais"
          mb="8"
          w="250px"
        />
      )}

      <Flex
        as="form"
        width="100%"
        maxWidth={488}
        flexDir="column"
        p="8"
        rounded="2xl"
        boxShadow={['none', 'xs']}
        bg={[bgFormWideVersion, bgForm]}
        onSubmit={handleSubmit(handleSignIn)}
      >
        <Stack spacing="4">
          <Flex justify="space-between">
            <div />
            <Text
              color={color}
              fontSize="sm"
              fontWeight="medium"
              align="center"
            >
              Entre na sua conta
            </Text>

            <Icon
              as={colorMode === 'light' ? LuSun : LuMoon}
              color={colorIcon}
              fontSize={[18, 22]}
              onClick={toggleColorMode}
              cursor="pointer"
            />
          </Flex>

          <Input
            type="email"
            label="Digitar email"
            error={errors.email}
            leftIcon={LuAtSign}
            {...register('email')}
          />

          <Input
            type="password"
            label="Digitar senha"
            error={errors.password}
            leftIcon={LuKeyRound}
            {...register('password')}
          />
        </Stack>

        <Button type="submit" mt="6" isLoading={isSubmitting}>
          Entrar
        </Button>

        <Divider my="6" borderColor={color} />

        <HStack justify="center">
          <Link to="/forgot">
            <ChakraLink as="div" color={link} fontSize="sm">
              Não consegue entrar?
            </ChakraLink>
          </Link>
          <Text color={color}>•</Text>
          <Link to="/create-account">
            <ChakraLink as="div" color={link} fontSize="sm">
              Criar uma conta
            </ChakraLink>
          </Link>
        </HStack>
      </Flex>
    </Flex>
  );
};

export default SignIn;
